import { Navigate, Route, Routes } from "react-router";

import { BuylistScreen } from "@/features/buylist/BuylistScreen";
import { ConsignmentIntakeScreen } from "@/features/consignment/ConsignmentIntakeScreen";
import { RequireOnline, SaleOrOffline } from "@/features/offline/RequireOnline";
import { SyncScreen } from "@/features/offline/SyncScreen";
import { HoldsScreen } from "@/features/holds/HoldsScreen";
import { PickupScreen } from "@/features/pickup/PickupScreen";
import { ReceiptHistory } from "@/features/receipt/ReceiptHistory";
import { ReceiptScreen } from "@/features/receipt/ReceiptScreen";
import { ReturnScreen } from "@/features/returns/ReturnScreen";
import { SaleScreen } from "@/features/sale/SaleScreen";
import { SettingsScreen } from "@/features/settings/SettingsScreen";
import { ShiftScreen } from "@/features/shift/ShiftScreen";
import { TaxFreeWizard } from "@/features/taxfree/TaxFreeWizard";
import { DoneScreen } from "@/features/tender/DoneScreen";
import { TenderScreen } from "@/features/tender/TenderScreen";

import { RequireFeature, RequireShift } from "./guards";
import { Layout } from "./layout";

export interface AppRoutesProps {
  updates?: Parameters<typeof Layout>[0]["updates"];
  onUpdate?: () => void;
}

/**
 * Screens of the register (T07-22 router). Offline (T07-33): 販売 becomes the offline basket (cash
 * only); the screens that need the server say so instead of failing (RequireOnline).
 */
export function AppRoutes({ updates, onUpdate }: AppRoutesProps) {
  return (
    <Routes>
      <Route
        element={<Layout {...(updates ? { updates } : {})} {...(onUpdate ? { onUpdate } : {})} />}
      >
        <Route
          index
          element={
            <RequireShift>
              <SaleOrOffline>
                <SaleScreen />
              </SaleOrOffline>
            </RequireShift>
          }
        />
        <Route
          path="pay"
          element={
            <RequireShift>
              <RequireOnline titleKey="tender.title">
                <TenderScreen />
              </RequireOnline>
            </RequireShift>
          }
        />
        <Route path="done/:saleId" element={<DoneScreen />} />
        <Route
          path="receipts"
          element={
            <RequireOnline titleKey="nav.history">
              <ReceiptHistory />
            </RequireOnline>
          }
        />
        <Route
          path="receipts/:saleId"
          element={
            <RequireOnline titleKey="nav.history">
              <ReceiptScreen />
            </RequireOnline>
          }
        />
        <Route
          path="shift"
          element={
            <RequireOnline titleKey="nav.shift">
              <ShiftScreen />
            </RequireOnline>
          }
        />
        <Route path="settings" element={<SettingsScreen />} />
        <Route
          path="hold"
          element={
            <RequireShift>
              <RequireOnline titleKey="nav.hold">
                <HoldsScreen />
              </RequireOnline>
            </RequireShift>
          }
        />
        <Route
          path="returns"
          element={
            <RequireShift>
              <RequireOnline titleKey="nav.return">
                <ReturnScreen />
              </RequireOnline>
            </RequireShift>
          }
        />
        <Route
          path="pickup"
          element={
            <RequireShift>
              <RequireOnline titleKey="nav.pickup">
                <PickupScreen />
              </RequireOnline>
            </RequireShift>
          }
        />
        <Route
          path="tax-free"
          element={
            <RequireFeature feature="taxFree">
              <RequireShift>
                <RequireOnline titleKey="nav.tax_free">
                  <TaxFreeWizard />
                </RequireOnline>
              </RequireShift>
            </RequireFeature>
          }
        />
        <Route
          path="buylist"
          element={
            <RequireShift>
              <RequireOnline titleKey="nav.buylist">
                <BuylistScreen />
              </RequireOnline>
            </RequireShift>
          }
        />
        <Route
          path="consignment"
          element={
            <RequireOnline titleKey="nav.consignment">
              <ConsignmentIntakeScreen />
            </RequireOnline>
          }
        />
        <Route path="sync" element={<SyncScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
